import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import countBy from 'lodash/countBy';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { collectAll } from '@atlassian/experience-tracker';
import { ff } from '@atlassian/jira-feature-flagging';
import Placeholder from '@atlassian/jira-placeholder';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { ProjectContext } from '@atlassian/jira-providers-project-context/src/types';
import { SubmitApdex } from '@atlassian/jira-providers-spa-apdex-analytics';
import MAIN_CODE_PAGE_QUERY, {
	type mainCodeInJiraUpdatedQuery,
	type mainCodeInJiraUpdatedQuery$data,
} from '@atlassian/jira-relay/src/__generated__/mainCodeInJiraUpdatedQuery.graphql';
import { Redirect, useRouter } from '@atlassian/jira-router';
import { DataDepotProviderIds, VcsProviderIds } from '../common/constants';
import { toProjectAri } from '../common/types';
import { getPullRequestsUpdatedFromDate, getNumPullRequestsToFetch } from '../common/utils';
import { codePageLoad } from '../common/utils/performance-analytics';
import { useCodeFeatureState, codeFeatureStates } from '../controllers/code-feature-store';
import {
	CodePageExperienceStart,
	VIEW_CODE_MAIN_PAGE_EXPERIENCE_NAME,
	VIEW_CONNECTION_PAGE_EXPERIENCE_NAME,
	VIEW_PR_LIST_EXPERIENCE_NAME,
	VIEW_REPO_LIST_EXPERIENCE_NAME,
} from '../services/experience-tracker';
import Header from './header';
import LoadingPage from './loading';
import { MainPage, ConnectionPage } from './pages';

export type Props = {
	cloudId: string;
	initialQueryReference: PreloadedQuery<mainCodeInJiraUpdatedQuery>;
	isAdmin: boolean;
	projectContext: ProjectContext;
};

// @Export for testing
export const getRepoCountByProvider = (data: mainCodeInJiraUpdatedQuery$data) => {
	const repositories = data.jira?.jiraProjectByKey?.repositories?.edges;
	return repositories ? countBy(repositories, 'node.properties.codeInJira.providerId') : null;
};

const isBitbucketConfigured = (data: mainCodeInJiraUpdatedQuery$data) =>
	// There will be a config state entry for each DVCS bitbucket account connected, so we check that *any* of these are configured
	data.jira?.devOps?.configState?.config?.nodes?.some(
		(config) => config?.appId === VcsProviderIds.BITBUCKET && config?.status === 'CONFIGURED',
	) ?? false;

const getRepoCountSummary = (data: mainCodeInJiraUpdatedQuery$data) =>
	data.jira?.jiraProjectByKey?.repositories?.edges?.length ?? 0;

const getProviderCount = (data: mainCodeInJiraUpdatedQuery$data) => {
	let providerCount =
		data.devOps?.providers?.devInfoProviders?.filter(
			// Filter out bitbucket since we check for this separately
			(provider) =>
				provider?.id !== DataDepotProviderIds.BITBUCKET_PROD &&
				provider?.id !== DataDepotProviderIds.BITBUCKET_STG,
		).length ?? 0;
	if (isBitbucketConfigured(data)) providerCount++;
	return providerCount;
};

export const CodePageRootRelay = ({
	projectContext,
	isAdmin,
	cloudId,
	initialQueryReference,
}: Props) => {
	const { isProjectAdmin, projectId, projectKey, simplified } = projectContext;
	const featureState = useCodeFeatureState();

	const [queryReference, loadQuery] = useQueryLoader<mainCodeInJiraUpdatedQuery>(
		MAIN_CODE_PAGE_QUERY,
		initialQueryReference,
	);

	const [{ match }] = useRouter();

	const siteAri = useMemo(() => JiraSiteAri.create({ siteId: cloudId }).toString(), [cloudId]);

	const onRefetch = useCallback(() => {
		loadQuery(
			{
				cloudId,
				projectKey,
				pullRequestsUpdatedFrom: getPullRequestsUpdatedFromDate(),
				numPullRequestsToFetch: getNumPullRequestsToFetch(match.query),
				siteAri,
			},
			{ fetchPolicy: 'network-only' },
		);
	}, [loadQuery, cloudId, projectKey, match.query, siteAri]);

	const projectAri = toProjectAri(cloudId, projectId);

	const data = usePreloadedQuery<mainCodeInJiraUpdatedQuery>(
		graphql`
			query mainCodeInJiraUpdatedQuery(
				$cloudId: ID!
				$projectKey: String!
				$numPullRequestsToFetch: Int!
				$pullRequestsUpdatedFrom: DateTime!
				$siteAri: ID!
			) {
				devOps @required(action: THROW) {
					providers(id: $siteAri, providerTypes: [DEV_INFO])
						@optIn(to: "DevOpsSecurityInJira")
						@required(action: THROW) {
						devInfoProviders {
							id
							__typename
						}
					}
				}
				codeInJira(cloudId: $cloudId) @required(action: THROW) {
					userConfiguration @required(action: THROW) {
						ownedBitbucketWorkspaces @required(action: THROW) {
							__typename
						}
					}
				}
				jira {
					devOps {
						# It is not enough to fetch all providers from data depot alone, since bitbucket is installed
						# by default on all jira instances. We also need to check that bitbucket, in particular, is configured.
						# We are unable to use hydration in the above devOps query because the appId returned from data depot, while correct,
						# does not match the hardcoded "bitbucket" value Jira monolith's config service is expecting.
						configState(cloudId: $cloudId, appId: "bitbucket") @optIn(to: "Jira-config-state") {
							config {
								nodes {
									appId
									status
								}
							}
						}
					}
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) {
						repositories(sort: { lastModified: { direction: DESC, priority: 1 } })
							@optIn(to: "GraphStoreProjectAssociatedRepo") {
							edges {
								__typename
							}
						}
					}
				}
				...main_softwareCodePage_MainPage
				...main_softwareCodePage_Header
				...main_softwareCodePage_ConnectionPage
			}
		`,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		queryReference as PreloadedQuery<mainCodeInJiraUpdatedQuery>,
	);

	if (featureState === codeFeatureStates.DISABLED) {
		return <Redirect to={`/projects/${projectKey}`} />;
	}

	if (featureState === codeFeatureStates.LOADING) {
		return <LoadingPage />;
	}

	const providerCount = getProviderCount(data);

	return (
		<ContextualAnalyticsData
			sourceName="codeMain"
			sourceType={SCREEN}
			containerId={String(projectId)}
			containerType="project"
			attributes={{
				projectId,
				nextgen: simplified,
				isJiraGlobalAdmin: isAdmin,
				repoCount: getRepoCountByProvider(data),
				repoCountSummary: getRepoCountSummary(data),
				bitbucketWorkspacesCount: data.codeInJira.userConfiguration.ownedBitbucketWorkspaces.length,
				isProjectAdmin,
				numberOfConnectedProviders: providerCount,
			}}
		>
			<PageWrapper>
				<FireScreenAnalytics />
				{!ff('platform.atlassian.griffin-navigation-redesign') && (
					<Header projectContext={projectContext} headerData={data} isAdmin={isAdmin} />
				)}
				{providerCount > 0 ? (
					<>
						<CodePageExperienceStart
							experienceId="codePageMainPage"
							experienceName={VIEW_CODE_MAIN_PAGE_EXPERIENCE_NAME}
							collect={collectAll([VIEW_REPO_LIST_EXPERIENCE_NAME, VIEW_PR_LIST_EXPERIENCE_NAME])}
						/>
						<MainPage
							mainPageData={data}
							projectAri={projectAri}
							onRefetch={onRefetch}
							isAdmin={isAdmin}
							projectContext={projectContext}
						/>
					</>
				) : (
					<>
						<CodePageExperienceStart
							experienceId="codePageConnectionPage"
							experienceName={VIEW_CONNECTION_PAGE_EXPERIENCE_NAME}
						/>
						<ConnectionPage connectionPageData={data} isAdmin={isAdmin} onRefetch={onRefetch} />
					</>
				)}
				{/* @ts-expect-error - TS2769 - No overload matches this call. */}
				<SubmitApdex appName="code-page" metric={codePageLoad} />
			</PageWrapper>
		</ContextualAnalyticsData>
	);
};

export const CodePageRoot = (props: Props) => (
	<Placeholder name="code-page-root" fallback={<LoadingPage />}>
		<CodePageRootRelay {...props} />
	</Placeholder>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
});
